import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import courseService from "../../services/courseService";
import { queryKeys } from "./queryKeys";

// Courses
export const useCourses = (filters = {}) => {
  return useQuery({
    queryKey: queryKeys.courses.list(filters),
    queryFn: () => courseService.getCourses(filters),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCourse = (id) => {
  return useQuery({
    queryKey: queryKeys.courses.detail(id),
    queryFn: () => courseService.getCourseById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCoursesByDepartment = (departmentId) => {
  return useQuery({
    queryKey: queryKeys.courses.byDepartment(departmentId),
    queryFn: () => courseService.getCoursesByDepartment(departmentId),
    enabled: !!departmentId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateCourse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (courseData) => courseService.createCourse(courseData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.courses.lists(),
      });
    },
  });
};

export const useUpdateCourse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, courseData }) =>
      courseService.updateCourse(id, courseData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.courses.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.courses.lists(),
      });
    },
  });
};

export const useDeleteCourse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => courseService.deleteCourse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.courses.lists(),
      });
    },
  });
};

export const useImportCourses = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (fileData) => courseService.importCourses(fileData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.courses.lists(),
      });
    },
  });
};

export const useExportCourses = () => {
  return useMutation({
    mutationFn: (params) => courseService.exportCourses(params),
  });
};

// Subjects
export const useSubjects = (filters = {}) => {
  return useQuery({
    queryKey: queryKeys.subjects.list(filters),
    queryFn: () => courseService.getAllSubjects(filters),
    staleTime: 5 * 60 * 1000,
  });
};

export const useAllSubjects = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['subjects', 'all', params],
    queryFn: () => courseService.getAllSubjects(params),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    enabled: true,
    ...options,
  });
};

export const useSubject = (id) => {
  return useQuery({
    queryKey: queryKeys.subjects.detail(id),
    queryFn: () => courseService.getSubjectById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useSubjectsByCourse = (courseId) => {
  return useQuery({
    queryKey: queryKeys.subjects.byCourse(courseId),
    queryFn: () => courseService.getSubjectsByCourse(courseId),
    enabled: !!courseId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useSubjectsByTeacher = (teacherId) => {
  return useQuery({
    queryKey: [...queryKeys.subjects.all, "teacher", teacherId],
    queryFn: () => courseService.getSubjectsByTeacher(teacherId),
    enabled: !!teacherId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateSubject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (subjectData) => courseService.createSubject(subjectData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.subjects.lists(),
      });
    },
  });
};

export const useUpdateSubject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, subjectData }) =>
      courseService.updateSubject(id, subjectData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.subjects.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.subjects.lists(),
      });
    },
  });
};

export const useDeleteSubject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => courseService.deleteSubject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.subjects.lists(),
      });
    },
  });
};

export const useAssignTeacherToSubject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ subjectId, teacherId }) =>
      courseService.assignTeacherToSubject(subjectId, teacherId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.subjects.detail(variables.subjectId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.subjects.lists(),
      });
    },
  });
};

export const useImportSubjects = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (fileData) => courseService.importSubjects(fileData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.subjects.lists(),
      });
    },
  });
};

export const useExportSubjects = () => {
  return useMutation({
    mutationFn: (params) => courseService.exportSubjects(params),
  });
};

// Classes
export const useClasses = (filters = {}) => {
  return useQuery({
    queryKey: [...queryKeys.courses.all, "classes", filters],
    queryFn: () => courseService.getClasses(filters),
    staleTime: 5 * 60 * 1000,
  });
};

export const useClass = (id) => {
  return useQuery({
    queryKey: [...queryKeys.courses.all, "classes", "detail", id],
    queryFn: () => courseService.getClassById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateClass = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (classData) => courseService.createClass(classData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.courses.all, "classes"],
      });
    },
  });
};

export const useUpdateClass = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, classData }) =>
      courseService.updateClass(id, classData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.courses.all, "classes", "detail", variables.id],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.courses.all, "classes"],
      });
    },
  });
};

export const useDeleteClass = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => courseService.deleteClass(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.courses.all, "classes"],
      });
    },
  });
};

export const useAssignClassTeacher = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ classId, teacherId }) =>
      courseService.assignClassTeacher(classId, teacherId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.courses.all, "classes", "detail", variables.classId],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.courses.all, "classes"],
      });
    },
  });
};

// Get class names
export const useClassNames = () => {
  return useQuery({
    queryKey: [...queryKeys.courses.all, "class-names"],
    queryFn: () => courseService.getClassNames(),
    staleTime: 5 * 60 * 1000,
  });
};

// Get subject names
export const useSubjectNames = () => {
  return useQuery({
    queryKey: [...queryKeys.courses.all, "subject-names"],
    queryFn: () => courseService.getSubjectNames(),
    staleTime: 5 * 60 * 1000,
  });
};

